import React from "react"
import { SmartDateFormat } from "@util/dates"
import { format } from "date-fns"
import { formatPrice } from "../../../util/ticketUtils"
import { AlternatePricingDisplay } from "../AlternatePricingDisplay"
import Pluralize from "react-pluralize"

interface TicketEventInfoProps {
  concert: { [key: string]: any }
  ticketPrice: number
  ticketPriceMax?: number | null
  isSlidingScale?: boolean
  chosenPrice?: number | null
  onChosenPriceChange?: (p: number) => void
  quantity: number
  onQuantityChange: (q: number) => void
  maxTicketsAllowedToBuy: number
  ticketsPurchasedByUserCount?: number
  showQuantitySelector?: boolean
  // Once the purchase is confirmed, the pricing/quantity details no longer
  // matter - just show what event the ticket is for.
  checkoutCompleted?: boolean
}

export const TicketEventInfo: React.FC<TicketEventInfoProps> = ({
  concert,
  ticketPrice,
  ticketPriceMax,
  isSlidingScale,
  chosenPrice,
  onChosenPriceChange,
  quantity,
  onQuantityChange,
  maxTicketsAllowedToBuy,
  ticketsPurchasedByUserCount = 0,
  showQuantitySelector = true,
  checkoutCompleted = false,
}) => {
  const effectivePrice = isSlidingScale ? (chosenPrice ?? ticketPrice) : ticketPrice

  if (checkoutCompleted) {
    return (
      <div className="mb-4 bg-gray-50 rounded">
        <div className="font-black">{concert.title}</div>
        <div className="text-sm text-gray-800">
          {SmartDateFormat({ dateStr: concert.start_dt, includeTime: true })}~
          {format(new Date(concert.end_dt), "h:mma")}
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="mb-4 bg-gray-50 rounded">
        <div className="font-black">{concert.title}</div>
        <div className="font-bold mb-2 text-gray-800 text-sm uppercase">
          from {concert.band_name}
        </div>
        <div className="text-sm text-gray-800">
          {SmartDateFormat({ dateStr: concert.start_dt, includeTime: true })}~
          {format(new Date(concert.end_dt), "h:mma")}
          &nbsp;
        </div>
        {(concert.bn_venue || concert.venue_name || concert.venue_city_display_name) && (
          <div className="text-sm text-gray-800">
            {concert.bn_venue
              ? concert.bn_venue.name
              : concert.venue_name && concert.venue_city_display_name
                ? `${concert.venue_name} in ${concert.venue_city_display_name}`
                : concert.venue_name || concert.venue_city_display_name}
          </div>
        )}
        <div className="text-sm text-gray-800 font-semibold">
          {isSlidingScale && ticketPriceMax
            ? `${formatPrice(ticketPrice)}–${formatPrice(ticketPriceMax)}`
            : `${formatPrice(ticketPrice)}`}
          <AlternatePricingDisplay
            concert={concert}
            className="text-gray-500 font-normal"
          />
        </div>
      </div>

      {showQuantitySelector && (
        <>
          {ticketsPurchasedByUserCount > 0 && (
            <div className="mb-4">
              <div className="font-bold text-xs uppercase">
                You already have{" "}
                <Pluralize
                  singular="ticket"
                  plural="tickets"
                  count={ticketsPurchasedByUserCount}
                />
              </div>
            </div>
          )}
          <div className="mb-2 flex gap-4 items-end">
            <div className="flex-1">
              <label
                htmlFor="ticket-quantity"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                {ticketsPurchasedByUserCount > 0
                  ? "Number of Additional Tickets"
                  : "Number of Tickets"}
              </label>
              <select
                id="ticket-quantity"
                value={quantity}
                onChange={(e) => onQuantityChange(Number(e.target.value))}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:border-blue-500"
              >
                {Array.from(
                  { length: Math.max(1, maxTicketsAllowedToBuy) },
                  (_, i) => i + 1,
                ).map((num) => (
                  <option key={num} value={num}>
                    {num} {num === 1 ? "ticket" : "tickets"}
                  </option>
                ))}
              </select>
            </div>
            {isSlidingScale && onChosenPriceChange && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 text-right">
                  Your price
                </label>
                <div className="flex items-center gap-1">
                  <span className="text-sm text-gray-700">$</span>
                  <input
                    type="number"
                    min={ticketPrice}
                    max={ticketPriceMax ?? undefined}
                    step="1"
                    value={chosenPrice ?? ticketPrice}
                    onChange={(e) => onChosenPriceChange(parseFloat(e.target.value))}
                    className="border border-gray-300 rounded px-2 py-2 w-24 text-sm text-right"
                  />
                </div>
              </div>
            )}
          </div>
          <div className="mb-4 text-sm text-gray-600">
            Total: {formatPrice(effectivePrice * quantity)}
          </div>
        </>
      )}
    </>
  )
}

export default TicketEventInfo
